import { validFileTypes } from './annotationConstants';
import { v4 as uuid } from 'uuid';
import dropdownData from '../../utils/dropdownData.json';
import { Annotation as AnnotationType } from './annotationTypes';

export interface TransformedAnnotation extends AnnotationType {
  imageWidth: number;
  imageHeight: number;
}

export interface TransformedData {
  [imageId: string]: TransformedAnnotation[];
  // width?: number;
  // height?: number;
}

export const convertAnnotationsToImageCoordinates = (annotation: AnnotationType): AnnotationType => {
  const {
    id,
    rotation,
    x,
    y,
    x1,
    y1,
    x2,
    y2,
    type,
    width,
    height,
    deleted,
    action,
    review_status,
    created_by,
    frameWidth = 3840,
    frameHeight = 2160,
    imageWidth = 3840,
    imageHeight = 2160,
  } = annotation;

  return {
    id,
    rotation,
    x: parseFloat(((x * imageWidth) / frameWidth).toFixed(4)),
    y: parseFloat(((y * imageHeight) / frameHeight).toFixed(4)),
    x1: parseFloat(((x1 * imageWidth) / frameWidth).toFixed(4)),
    y1: parseFloat(((y1 * imageHeight) / frameHeight).toFixed(4)),
    x2: parseFloat(((x2 * imageWidth) / frameWidth).toFixed(4)),
    y2: parseFloat(((y2 * imageHeight) / frameHeight).toFixed(4)),
    type,
    width: parseFloat(((width * imageWidth) / frameWidth).toFixed(4)),
    height: parseFloat(((height * imageHeight) / frameHeight).toFixed(4)),
    imageWidth,
    imageHeight,
    deleted,
    action,
    review_status,
    created_by,
  };
};

export const prepareJSONData = (
  data: Record<string, AnnotationType[]>,
  displayedWidth: number,
  displayedHeight: number,
  imageWidth: number,
  imageHeight: number
): TransformedData => {
  const transformedData: TransformedData = {};

  for (const [imageId, annotations] of Object.entries(data)) {
    const finalAnnotations: TransformedAnnotation[] = annotations.map(annotation => {
      const { id, rotation, x, y, x1, y1, x2, y2, type, width, height, deleted, action, review_status, created_by } = annotation;

      return {
        id,
        rotation,
        x: parseFloat(((x * imageWidth) / displayedWidth).toFixed(4)),
        y: parseFloat(((y * imageHeight) / displayedHeight).toFixed(4)),
        x1: parseFloat(((x1 * imageWidth) / displayedWidth).toFixed(4)),
        y1: parseFloat(((y1 * imageHeight) / displayedHeight).toFixed(4)),
        x2: parseFloat(((x2 * imageWidth) / displayedWidth).toFixed(4)),
        y2: parseFloat(((y2 * imageHeight) / displayedHeight).toFixed(4)),
        type,
        width: parseFloat(((width * imageWidth) / displayedWidth).toFixed(4)),
        height: parseFloat(((height * imageHeight) / displayedHeight).toFixed(4)),
        imageWidth,
        imageHeight,
        deleted,
        action,
        review_status,
        created_by,
      };
    });

    transformedData[imageId] = finalAnnotations;
  }

  // Set once outside loop (cleaner)
  // transformedData.width = imageWidth;
  // transformedData.height = imageHeight;

  return transformedData;
};

// src/utils/getCloseButtonPositionOnLoad.ts

export interface AnnotationNode {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
}

interface Point {
  x: number;
  y: number;
}

export const getCloseButtonPositionOnLoad = (node: AnnotationNode): string => {
  // Ensure minimum size
  const wi = Math.max(5, node.width);

  const rotation = node.rotation ?? 0;
  const { x, y } = node;

  // Top-right corner before rotation
  const corners: Point[] = [{ x: x + wi, y: y }];

  // Rotate corner
  const rotatedCorners = corners.map(corner => rotatePoint(corner.x, corner.y, x, y, rotation));

  // Normalize and format
  const normalizedCorners = rotatedCorners.map(({ x, y }) => ({
    x: x.toFixed(6),
    y: y.toFixed(6),
  }));

  const obbString = normalizedCorners.map(({ x, y }) => `${x} ${y}`).join(' ');

  return obbString;
};

// src/utils/convertToYolovObb8.ts

export interface DamageClass {
  code: string;
  classId: string;
}

export interface DropdownData {
  damages: DamageClass[];
}

export const convertToYolovObb8 = (data: Annotation[], sWidth: number, sHeight: number, dropdownData: DropdownData): string[] => {
  return data.map(annotation => {
    const width = Math.abs(annotation.x2 - annotation.x1);
    const height = Math.abs(annotation.y2 - annotation.y1);

    const rotation = annotation.rotation ?? 0;

    const x = annotation.x1;
    const y = annotation.y1;

    // Rectangle corners before rotation
    const corners: Point[] = [
      { x: x, y: y }, // Top-left
      { x: x + width, y: y }, // Top-right
      { x: x + width, y: y + height }, // Bottom-right
      { x: x, y: y + height }, // Bottom-left
    ];

    // Rotate corners around top-left (same as your original logic)
    const rotatedCorners = corners.map(corner => rotatePoint(corner.x, corner.y, x, y, rotation));

    // Normalize relative to canvas size
    const normalizedCorners = rotatedCorners.map(({ x, y }) => ({
      x: (x / sWidth).toFixed(6),
      y: (y / sHeight).toFixed(6),
    }));

    // Extract class code safely
    const classCode = annotation.type?.substring(2, 4) ?? '';

    const classData = dropdownData.damages.find(rs => rs.code === classCode);

    const classId = classData ? parseInt(classData.classId, 10) : 0;

    // YOLO OBB format: class_id x1 y1 x2 y2 x3 y3 x4 y4
    const obbString = `${classId} ${normalizedCorners.map(({ x, y }) => `${x} ${y}`).join(' ')}`;

    return obbString;
  });
};
/* ---------------------------------- TYPES --------------------------------- */

export interface Annotation {
  id: string;
  rotation?: number;
  x: number;
  y: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  type: string;
  width?: number;
  height?: number;
  deleted?: boolean;
  action?: string;
  review_status?: string;
  created_by?: string;
}

export type AnnotationData = Record<string, Annotation[]>;

interface DropdownItem {
  code: string;
  classId?: string;
  name?: string;
}

/* -------------------------- FILE TYPE VALIDATION --------------------------- */

export const isValidUploadedFileType = (files: File | File[]): boolean => {
  return Array.isArray(files) ? files.every(file => validFileTypes.includes(file.type)) : validFileTypes.includes(files.type);
};

/* ---------------------- JSON → DOWNLOAD TRANSFORMER ------------------------ */

export const prepareJSONDataOBB = (
  data: AnnotationData,
  displayedWidth: number,
  displayedHeight: number,
  imageWidth: number,
  imageHeight: number
) => {
  const scaleX = imageWidth / displayedWidth;
  const scaleY = imageHeight / displayedHeight;

  const result: Record<string, Annotation[]> & {
    width?: number;
    height?: number;
  } = {};

  for (const [imageId, annotations] of Object.entries(data)) {
    result[imageId] = annotations.map(a => ({
      ...a,
      x: +(a.x * scaleX).toFixed(4),
      y: +(a.y * scaleY).toFixed(4),
      x1: +(a.x1 * scaleX).toFixed(4),
      y1: +(a.y1 * scaleY).toFixed(4),
      x2: +(a.x2 * scaleX).toFixed(4),
      y2: +(a.y2 * scaleY).toFixed(4),
      width: a.width ? +(a.width * scaleX).toFixed(4) : undefined,
      height: a.height ? +(a.height * scaleY).toFixed(4) : undefined,
    }));
  }

  result.width = imageWidth;
  result.height = imageHeight;

  return result;
};

/* --------------------- JSON → KONVA COMPATIBLE ----------------------------- */

export const transformJSONDataToKonvaCompatible = (data: AnnotationData): AnnotationData => {
  const result: AnnotationData = {};

  for (const [imageId, annotations] of Object.entries(data)) {
    result[imageId] = annotations.map(a => ({
      ...a,
      id: uuid(),
      x: a.x1,
      y: a.y1,
      width: a.x2 - a.x1,
      height: a.y2 - a.y1,
    }));
  }

  return result;
};

/* -------------------------- COLOR UTILITIES ------------------------------- */

export const getLuminance = (hex: string): number => {
  const rgb = parseInt(hex.slice(1), 16);
  const r = (rgb >> 16) & 255;
  const g = (rgb >> 8) & 255;
  const b = rgb & 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const getTextColor = (bgColor: string): 'black' | 'white' => (getLuminance(bgColor) > 128 ? 'black' : 'white');

/* ---------------------------- YOLO HELPERS -------------------------------- */

export const rotatePoint = (px: number, py: number, cx: number, cy: number, angle: number): Point => {
  const rad = (Math.PI / 180) * angle;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: cos * (px - cx) - sin * (py - cy) + cx,
    y: sin * (px - cx) + cos * (py - cy) + cy,
  };
};

const findClassId = (list: DropdownItem[], code: string): number => parseInt(list.find(d => d.code === code)?.classId ?? '0');

/* ------------------------- YOLOv8 (BBox) ---------------------------------- */

export const convertToYolov8 = (data: Annotation[], sWidth: number, sHeight: number): string[] =>
  data.map(a => {
    const cls = findClassId(dropdownData.damages, a.type.substring(2, 4));
    const x = (a.x1 + a.x2) / 2 / sWidth;
    const y = (a.y1 + a.y2) / 2 / sHeight;
    const w = Math.abs(a.x2 - a.x1) / sWidth;
    const h = Math.abs(a.y2 - a.y1) / sHeight;
    return `${cls} ${x.toFixed(6)} ${y.toFixed(6)} ${w.toFixed(6)} ${h.toFixed(6)}`;
  });

/* ------------------------- YOLOv8 (OBB) ----------------------------------- */

export const convertAnnotationToOBBYolov8 = (a: AnnotationType, sWidth: number, sHeight: number, damageTypes: any): string => {
  const w = Math.abs(a.x2 - a.x1);
  const h = Math.abs(a.y2 - a.y1);
  const rot = a.rotation ?? 0;

  const corners = [
    { x: a.x1, y: a.y1 },
    { x: a.x1 + w, y: a.y1 },
    { x: a.x1 + w, y: a.y1 + h },
    { x: a.x1, y: a.y1 + h },
  ].map(p => rotatePoint(p.x, p.y, a.x1, a.y1, rot));

  const cls = damageTypes.items.find((item: any) => item.code === a.type.substring(2, 4))?.classId;

  return `${cls} ${corners.map(p => `${(p.x / sWidth).toFixed(6)} ${(p.y / sHeight).toFixed(6)}`).join(' ')}`;
};

/* ----------------------------- BASE64 UTILS ------------------------------- */

export const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteString = atob(base64.split(',')[1]);
  const buffer = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    buffer[i] = byteString.charCodeAt(i);
  }
  return new Blob([buffer], { type: mimeType });
};

export const fileDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** Infer canvas export mime/quality from the intended download filename or source URL. */
export const getImageDownloadExportOptions = (
  fileNameOrUrl?: string | null
): { mimeType: string; quality?: number; extension: string } => {
  const path = String(fileNameOrUrl ?? '').split('?')[0];
  const extension = path.match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toLowerCase();

  if (extension === 'jpg' || extension === 'jpeg') {
    return {
      mimeType: 'image/jpeg',
      quality: 0.95,
      extension: `.${extension}`,
    };
  }

  if (extension === 'webp') {
    return {
      mimeType: 'image/webp',
      quality: 0.95,
      extension: '.webp',
    };
  }

  return {
    mimeType: 'image/png',
    extension: extension ? `.${extension}` : '.png',
  };
};

/* ------------------------------ FORMATTER -------------------------------- */

export const formatAAIG = (aiag?: string): string => {
  if (!aiag) return '';

  const getName = (arr: DropdownItem[], code: string) => arr.find(i => i.code === code)?.name ?? 'Empty';

  return [
    getName(dropdownData.parts, aiag.substring(0, 2)),
    getName(dropdownData.damages, aiag.substring(2, 4)),
    getName(dropdownData.severity, aiag.substring(4, 5)),
  ].join('-');
};
